<template>
  <v-row>
    <v-col class="mt-2" cols="12" offset-lg="1" lg="10">
      <v-breadcrumbs :items="breadcrumbs" divider=">" class="pl-1 pb-3" />
      <v-card dark flat>
        <v-card-title class="theme">
          <v-icon class="white--text">list</v-icon>
          <span class="headline ml-2 white--text">Add Timeoff Policy</span>
        </v-card-title>
      </v-card>
      <v-card>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-container>
              <v-row>
                <v-col class="pt-0" cols="12">
                  <div class="headline">General Settings</div>
                  <small>General settings for all the timeoff policies</small>
                </v-col>
                <v-col cols="12" lg="5">
                  <v-text-field
                    v-model="timeoff.name"
                    label="Name *"
                    validate-on-blur
                    :rules="[validationRules.required]"
                    hint="Name of this policy"
                    :disabled="tempDisable"
                    :persistent-hint="true"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small class="grey--text pt-1" v-on="on"
                            >help</v-icon
                          >
                        </template>
                        For e.g. Norway - Vacation policy.
                        <br />
                        This will be visible to the customers trying to use this
                        policy
                      </v-tooltip>
                    </template>
                  </v-text-field>

                  <v-checkbox
                    v-model="timeoff.count_holidays"
                    label="Count as calendar days"
                    hint="Should the weekends and holidays be treated as calendar days."
                    :disabled="tempDisable"
                    persistent-hint
                    class="my-4"
                  />

                  <v-autocomplete
                    v-model="timeoff.type"
                    label="Type *"
                    :items="setup.types"
                    validate-on-blur
                    :rules="[validationRules.required]"
                    hint="type for this policy"
                    :disabled="tempDisable"
                    :persistent-hint="true"
                    class="my-4"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small class="grey--text pt-1" v-on="on"
                            >help</v-icon
                          >
                        </template>
                        Vacation, Sick Leave or others
                      </v-tooltip>
                    </template>
                  </v-autocomplete>

                  <v-autocomplete
                    :items="countries"
                    v-model="timeoff.country"
                    item-text="name"
                    item-value="name"
                    :disabled="tempDisable"
                    label="Country *"
                    validate-on-blur
                    :rules="[validationRules.required]"
                    hint="Select the location where this policy is applicable"
                    :persistent-hint="true"
                    clearable
                    class="my-4"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small class="grey--text pt-1" v-on="on"
                            >help</v-icon
                          >
                        </template>
                        Please select the Country where you want to apply this
                        policy.
                      </v-tooltip>
                    </template>
                  </v-autocomplete>

                  <v-textarea
                    rows="3"
                    v-model="timeoff.description"
                    :disabled="tempDisable"
                    label="Description *"
                    hint="Some details for this policy"
                    :persistent-hint="true"
                    clearable
                    class="my-4"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small class="grey--text pt-1" v-on="on"
                            >help</v-icon
                          >
                        </template>
                        Description about this vacation to help your employees.
                      </v-tooltip>
                    </template>
                  </v-textarea>

                  <v-text-field
                    label="Start date for year*"
                    v-mask="masks.timeoffStartOfYear"
                    hint="in dd-mm format"
                    :persistent-hint="true"
                    :disabled="tempDisable"
                    validate-on-blur
                    clearable
                    :rules="[
                      timeoff && validationRules.required,
                      timeoff && validationRules.timeoffStartOfYear,
                    ]"
                    v-model="timeoff.start_date"
                    class="my-4"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small class="grey--text pt-1" v-on="on"
                            >help</v-icon
                          >
                        </template>
                        This will be used to mark the start of the year and<br />
                        calculate the carryover leaves for employees
                      </v-tooltip>
                    </template>
                  </v-text-field>

                  <v-text-field
                    :disabled="tempDisable"
                    v-model="timeoff.days"
                    v-mask="['###']"
                    type="number"
                    min="1"
                    max="365"
                    clearable
                    hint="number of days under this timeoff type"
                    :persistent-hint="true"
                    class="my-4"
                    :rules="[
                      validationRules.required,
                      validationRules.timeoffDefaultVacationDays,
                    ]"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small class="grey--text pt-1" v-on="on"
                            >help</v-icon
                          >
                        </template>
                        Maximum number of days employees can ask for this
                        timeoff.
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" lg="7"></v-col>

                <v-col cols="12">
                  <v-divider />
                  <v-row>
                    <v-col cols="6">
                      <div class="headline mt-3">Timeoff accrual policy</div>
                      <small
                        >At what frequency do employees earn timeoff under this
                        policy</small
                      >
                    </v-col>
                    <v-col cols="6">
                      <v-switch
                        style="float: right"
                        v-model="timeoff.accrual.enabled"
                        color="primary"
                        :disabled="true"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" lg="5">
                  <v-autocomplete
                    :items="setup.accrual.frequency"
                    v-model="timeoff.accrual.data.frequency"
                    label="Frequency *"
                    validate-on-blur
                    :rules="[
                      requiredWithField(
                        timeoff.accrual.enabled,
                        timeoff.accrual.data.frequency
                      ),
                    ]"
                    hint="At which the employees earn timeoff"
                    :persistent-hint="true"
                    clearable
                    :disabled="!timeoff.accrual.enabled"
                    class="my-4"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small class="grey--text pt-1" v-on="on"
                            >help</v-icon
                          >
                        </template>
                        Specify how frequently employees earn the timeoff.
                      </v-tooltip>
                    </template>
                  </v-autocomplete>

                  <v-autocomplete
                    :items="setup.accrual.timing"
                    v-model="timeoff.accrual.data.timing"
                    label="Accruals are earned at *"
                    validate-on-blur
                    :rules="[
                      requiredWithField(
                        timeoff.accrual.enabled,
                        timeoff.accrual.data.timing
                      ),
                    ]"
                    hint="Beginning or End of the period"
                    :persistent-hint="true"
                    clearable
                    :disabled="!timeoff.accrual.enabled"
                    class="my-4"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small class="grey--text pt-1" v-on="on"
                            >help</v-icon
                          >
                        </template>
                        Specify if the accruals are earned at the start of the
                        <br />period or end of the period.
                      </v-tooltip>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" lg="7"></v-col>

                <v-col cols="12">
                  <v-divider />
                  <v-row>
                    <v-col cols="6">
                      <div class="headline mt-3">Restrictions on usage</div>
                      <small
                        >Put a cap on the usage of this timeoff policy</small
                      >
                    </v-col>
                    <v-col cols="6">
                      <v-switch
                        style="float: right"
                        v-model="timeoff.restrictions.enabled"
                        color="primary"
                        :disabled="tempDisable"
                      />
                    </v-col>
                  </v-row>
                </v-col>

                <v-col cols="12" lg="5">
                  <v-text-field
                    clearable
                    label="Cap request"
                    v-mask="['###']"
                    type="number"
                    min="1"
                    max="365"
                    hint="Maximum number of timeoff requests per cap duration "
                    :persistent-hint="true"
                    :disabled="!timeoff.restrictions.enabled"
                    validate-on-blur
                    :rules="[
                      requiredWithField(
                        timeoff.restrictions.enabled,
                        timeoff.restrictions.data.cap_request
                      ),
                    ]"
                    v-model="timeoff.restrictions.data.cap_request"
                    class="my-4"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small class="grey--text pt-1" v-on="on"
                            >help</v-icon
                          >
                        </template>
                        Specify the number of times this timeoff can be
                        requested by
                        <br />the employee within a year.
                      </v-tooltip>
                    </template>
                  </v-text-field>

                  <v-text-field
                    clearable
                    label="Cap days"
                    hint="Maximum number of days per cap request"
                    v-mask="['###']"
                    type="number"
                    min="1"
                    max="365"
                    :persistent-hint="true"
                    :disabled="!timeoff.restrictions.enabled"
                    validate-on-blur
                    :rules="[
                      requiredWithField(
                        timeoff.restrictions.enabled,
                        timeoff.restrictions.data.cap_days
                      ),
                    ]"
                    v-model="timeoff.restrictions.data.cap_days"
                    class="my-4"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small class="grey--text pt-1" v-on="on"
                            >help</v-icon
                          >
                        </template>
                        Specify the maximum number of days that can be taken per
                        cap
                        <br />request.
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" lg="7"></v-col>

                <v-col cols="12">
                  <v-divider />
                  <v-row>
                    <v-col cols="6">
                      <div class="headline mt-3">Timeoff carryover</div>
                      <small
                        >Configure the available days that can be carried over
                        to next period</small
                      >
                    </v-col>
                    <v-col cols="6">
                      <v-switch
                        style="float: right"
                        v-model="timeoff.carryover.enabled"
                        color="primary"
                        :disabled="tempDisable"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-row>
                  <v-col class="pl-6" cols="12" lg="5">
                    <v-text-field
                      v-model="timeoff.carryover.data.balance_limit_days"
                      label="Number of carryover days *"
                      hint="Employees have a limited amount of time-off days at any given time."
                      clearable
                      v-mask="['###']"
                    type="number"
                    min="1"
                    max="365"
                    validate-on-blur
                      class="my-4"
                      :rules="[
                        requiredWithField(
                          timeoff.carryover.enabled,
                          timeoff.carryover.data.balance_limit_days
                        ),
                      ]"
                      :persistent-hint="true"
                      :disabled="!timeoff.carryover.enabled"
                    >
                      <template v-slot:append-outer>
                        <v-tooltip right>
                          <template v-slot:activator="{ on }">
                            <v-icon small class="grey--text pt-1" v-on="on"
                              >help</v-icon
                            >
                          </template>
                          Number of unused timeoff days that can be carried over
                          to next year.
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="7"></v-col>
                </v-row>
              </v-row>
              <v-btn
                depressed
                class="save white--text ml-0 mt-6 rounded-sm"
                :disabled="tempDisable"
                @click="save"
                >save
              </v-btn>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
    <v-snackbar v-model="snackbar" :bottom="true" :right="true">
      {{ snackbarMessage }}
      <v-btn color="pink" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-row>
</template>
<script>
import { mask } from "vue-the-mask";
import { mapGetters } from "vuex";

import { Actions } from "@/views/actions";
import { Breadcrumbs } from "@/views/breadcrumbs";
import { Component } from "@/views/component";
import { Constants } from "@/views/constants";

export default {
  name: Component.Protected.Company.LeaveTypes.Add,
  directives: { mask },
  data: () => ({
    valid: false,
    tokenHeader: null,
    breadcrumbs: Breadcrumbs.Leaves.Add,
    tempDisable: false,
    timeoffDelegate: null,
    setup: {
      workdays: Constants.LeaveTypes.Messages.Workdays,
      accrual: {
        frequency: Constants.LeaveTypes.Messages.AccrualEarnFrequency,
        timing: Constants.LeaveTypes.Messages.AccrualEarnAt,
      },
      restrictions: {
        cap_duration: Constants.LeaveTypes.Messages.RestrictionPeriods,
      },
      carryover: { dates: Constants.LeaveTypes.Messages.CarryoverStartDate },
      approval: { types: Constants.LeaveTypes.Messages.ApprovalType },
      types: ["Vacation", "Sick Leave", "Other time off"],
    },
    timeoff: {
      name: "",
      country: "",
      description: "",
      days: 0,
      hours: 75,
      start_date: "",
      count_holidays: null,
      workdays: Constants.LeaveTypes.Messages.SelectedWorkdays,
      type: "Others",
      specify: "Specify",
      accrual: {
        enabled: false,
        data: { frequency: "Annual", timing: "Start" },
      },
      restrictions: {
        enabled: false,
        data: { cap_duration: "", cap_request: 0, cap_days: 0 },
      },
      prorate: {
        enabled: false,
        data: { allowed: "No", waiting_period: "No", waiting_duration: 0 },
      },
      carryover: {
        enabled: false,
        data: {
          annual_reset: false,
          annual_reset_date: Constants.LeaveTypes.Messages.Policy,
          unlimited_carryover: false,
          unlimited_carryover_date: Constants.LeaveTypes.Messages.Policy,
          balance_limit_required: false,
          balance_limit_days: 0,
          cap_required: false,
          cap_days: 0,
          cap_date: Constants.LeaveTypes.Messages.Policy,
          cap_with_deadline_required: false,
          cap_with_deadline_days_count: 0,
          cap_with_deadline_days_allowed: 0,
          cap_with_deadline_date: Constants.LeaveTypes.Messages.Policy,
        },
      },
      approval: "Single",
    },
  }),
  computed: {
    ...mapGetters(["userInfo", "countries", "adminLeavePolicies"]),
  },
  methods: {
    requiredWithField(dependent, current) {
      if (!dependent) return true;
      if (dependent && current) return true;
      if (dependent && !current) return "Field is required";
      return "Field is required";
    },
    requiredWithTwoFields(x, y, current) {
      if (!x || !y) return true;
      if (x && y && current) return true;
      if (x && y && !current) return "Field is required";
      return "Field is required";
    },
    createYourself() {
      this.tempDisable = false;
    },
    applyPolicy() {
      if (this.$refs.templateForm.validate()) {
        let yolo = { ...this.timeoffDelegate };
        const co = {
          enabled: false,
          data: {
            annual_reset: false,
            annual_reset_date: Constants.LeaveTypes.Messages.Policy,
            unlimited_carryover: false,
            unlimited_carryover_date: Constants.LeaveTypes.Messages.Policy,
            balance_limit_required: false,
            balance_limit_days: 0,
            cap_required: false,
            cap_days: 0,
            cap_date: Constants.LeaveTypes.Messages.Policy,
            cap_with_deadline_required: false,
            cap_with_deadline_days_count: 0,
            cap_with_deadline_days_allowed: 0,
            cap_with_deadline_date: Constants.LeaveTypes.Messages.Policy,
          },
        };
        const pr = {
          enabled: false,
          data: { allowed: "No", waiting_period: "No", waiting_duration: 0 },
        };
        const res = {
          enabled: false,
          data: { cap_duration: "", cap_request: 0, cap_days: 0 },
        };
        const acc = {
          enabled: false,
          data: { frequency: "Annual", timing: "Start" },
        };
        this.timeoff = {
          ...yolo,
          days: yolo.days ? parseInt(yolo.days) : 0,
          count_holidays:
            yolo.count_holidays === null ? false : yolo.count_holidays,
          approval: "Single",
          carryover: yolo.carryover.enabled === null ? co : yolo.carryover,
          prorate: yolo.prorate.enabled === null ? pr : yolo.prorate,
          restrictions:
            yolo.restrictions.enabled === null ? res : yolo.restrictions,
          // accrual: yolo.accrual.enabled === null ? acc : yolo.accrual,
          accrual: acc,
        };
        this.snackbar = true;
        this.snackbarMessage = Constants.LeaveTypes.Messages.Applied;
        this.tempDisable = false;
      }
    },
    save() {
      if (this.$refs.form.validate()) {
        this.tempDisable = true;
        let data = {
          ...this.timeoff,
          days: parseInt(this.timeoff.days),
          hours: this.timeoff.hours / 10,
        };
        console.log("before", data);
        data.prorate.data.waiting_duration = parseInt(
          data.prorate.data.waiting_duration
        );
        data.restrictions.data.cap_request = parseInt(
          data.restrictions.data.cap_request
        );
        data.restrictions.data.cap_days = parseInt(
          data.restrictions.data.cap_days
        );
        data.carryover.data.balance_limit_days = parseInt(
          data.carryover.data.balance_limit_days
        );
        data.carryover.data.cap_days = parseInt(data.carryover.data.cap_days);
        data.carryover.data.cap_with_deadline_days_count = parseInt(
          data.carryover.data.cap_with_deadline_days_count
        );
        data.carryover.data.cap_with_deadline_days_allowed = parseInt(
          data.carryover.data.cap_with_deadline_days_allowed
        );
        console.log(data);
        this.$store
          .dispatch(Actions.LeaveTypes.Add, data)
          .then((r) => {
            if (r.status === 200) {
              this.$router.push({
                name: Component.Protected.Company.LeaveTypes.List,
              });
            }
          })
          .catch((e) => {
            console.error(Constants.LeaveTypes.Messages.Error.Add, e);
            this.snackbarMessage = Constants.LeaveTypes.Messages.Error.Add;
            this.snackbar = true;
          });
      }
    },
    adminPolicies() {
      this.$store.dispatch(Actions.LeaveTypes.Admin).catch((e) => {
        console.error(Constants.LeaveTypes.Messages.Error.Admin, e);
        this.snackbar = true;
        this.snackbarMessage = Constants.LeaveTypes.Messages.Error.Admin;
      });
    },
    initialize() {
      if (this.userInfo.role !== Constants.Roles.HR) {
        this.$router.go(-1);
        return false;
      }
      this.adminPolicies();
    },
  },
  created() {
    if (this.userInfo && this.userInfo.role) {
      this.initialize();
    } else {
      this.$store.dispatch(Actions.Firebase.UserInfo).then(() => this.initialize());
    }
  },
};
</script>